/**
 * BybitAccountCard — PULSE-28 dashboard widget.
 *
 * One card that bundles four user-relevant facts:
 *   1. is an API key wired up at all?
 *   2. is it still accepted by Bybit?
 *   3. when does it expire?
 *   4. what's the current balance / available margin?
 *
 * Data is refreshed hourly by the `pulsar-bybit-health` cron and read
 * via /api/me/bybit-status. The widget is *purely visual* — Telegram
 * notifications + alert state are computed server-side, so the card
 * just renders `status.alerts` rather than re-deriving thresholds.
 *
 * The component is intentionally compact (one tile in the dashboard
 * right column) — deeper diagnostics live on /app/settings.
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle, CheckCircle2, Clock, KeyRound, ChevronRight,
} from 'lucide-react'

import { useBybitStatus, type BybitAccountStatus } from '@/hooks/useDashboardApi'
import { formatUsd, timeAgo } from '@/lib/format'
import { cn } from '@/lib/cn'

const EXPIRY_WARN_DAYS = 7  // mirror server-side _maybe_notify threshold

export function BybitAccountCard() {
  const { t } = useTranslation('app')
  const { data, isLoading, isError } = useBybitStatus()

  return (
    <div className="bg-ink p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-textFaint text-xs uppercase tracking-wider">
          {t('bybit.card.title')}
        </div>
        <Link
          to="/app/settings"
          className="text-textFaint hover:text-text inline-flex items-center gap-1 text-xs"
          aria-label={t('bybit.card.open_settings') as string}
        >
          {t('bybit.card.open_settings')} <ChevronRight size={12} />
        </Link>
      </div>

      {isLoading ? (
        <div className="h-20 grid place-items-center text-textFaint text-xs animate-pulse">
          —
        </div>
      ) : isError || !data ? (
        <div className="text-xs text-rose">
          {t('bybit.card.error', { msg: '—' })}
        </div>
      ) : (
        <CardBody status={data} />
      )}
    </div>
  )
}

// ─── Sub-renderers ───────────────────────────────────────────────────

function CardBody({ status }: { status: BybitAccountStatus }) {
  const { t, i18n } = useTranslation('app')

  // 1. No keys at all → friendly CTA, not an error.
  if (!status.has_api_keys) {
    return (
      <div className="space-y-2">
        <Headline
          icon={<KeyRound size={16} />}
          tone="amber"
          label={t('bybit.card.no_keys')}
        />
        <div className="text-xs text-textDim">
          {t('bybit.card.no_keys_hint')}
        </div>
      </div>
    )
  }

  // 2. Keys present but rejected by Bybit (red).
  if (!status.key_valid) {
    return (
      <div className="space-y-2">
        <Headline
          icon={<AlertTriangle size={16} />}
          tone="rose"
          label={t('bybit.card.key_invalid')}
        />
        <div className="text-xs text-textDim">
          {status.last_check_error || t('bybit.card.key_invalid_hint')}
        </div>
        {status.last_check_at && (
          <CheckedAt iso={status.last_check_at} />
        )}
      </div>
    )
  }

  // 3. Keys valid — show key/balance facts. Highlight near-expiry yellow.
  const expiringSoon =
    status.key_expires_in_days != null
    && status.key_expires_in_days <= EXPIRY_WARN_DAYS

  return (
    <div className="space-y-3">
      <Headline
        icon={
          expiringSoon
            ? <Clock size={16} />
            : <CheckCircle2 size={16} />
        }
        tone={expiringSoon ? 'amber' : 'pulse'}
        label={
          expiringSoon
            ? t('bybit.card.expiring_soon')
            : t('bybit.card.key_valid')
        }
      />

      {status.key_expires_at && (
        <div className="text-xs text-textDim">
          {t('bybit.card.key_expires_at', {
            date: new Date(status.key_expires_at).toLocaleDateString(
              i18n.language,
              { day: '2-digit', month: 'short', year: 'numeric' },
            ),
          })}
          {status.key_expires_in_days != null && (
            <span
              className={cn(
                'ml-2 font-mono tabular-nums',
                expiringSoon ? 'text-amber' : 'text-textFaint',
              )}
            >
              {status.key_expires_in_days < 1
                ? t('bybit.card.key_expires_today')
                : t('bybit.card.key_expires_in', {
                    days: Math.floor(status.key_expires_in_days),
                  })}
            </span>
          )}
        </div>
      )}

      <BalanceRow status={status} />

      {status.last_check_at && <CheckedAt iso={status.last_check_at} />}
    </div>
  )
}

function BalanceRow({ status }: { status: BybitAccountStatus }) {
  const { t } = useTranslation('app')
  // Balance might genuinely be 0 (demo / fresh account) — only hide if
  // we don't have a value at all (probe failed before walletbalance).
  if (status.balance_usdt == null) return null

  return (
    <div className="grid grid-cols-3 gap-px bg-line text-xs">
      <BalanceCell
        label={t('bybit.card.balance')}
        value={formatUsd(status.balance_usdt)}
      />
      <BalanceCell
        label={t('bybit.card.available')}
        value={
          status.balance_available_usdt != null
            ? formatUsd(status.balance_available_usdt)
            : '—'
        }
      />
      <BalanceCell
        label={t('bybit.card.unrealised')}
        value={
          status.balance_unrealised_pnl != null
            ? formatUsd(status.balance_unrealised_pnl, { sign: true })
            : '—'
        }
        tone={
          status.balance_unrealised_pnl == null ? 'neutral'
          : status.balance_unrealised_pnl > 0  ? 'pulse'
          : status.balance_unrealised_pnl < 0  ? 'rose'
          : 'neutral'
        }
      />
    </div>
  )
}

function BalanceCell({
  label, value, tone = 'neutral',
}: {
  label: string
  value: string
  tone?: 'neutral' | 'pulse' | 'rose'
}) {
  return (
    <div className="bg-ink py-1.5 px-2">
      <div className="text-textFaint">{label}</div>
      <div
        className={cn(
          'font-mono tabular-nums mt-0.5',
          tone === 'pulse' ? 'text-pulse'
          : tone === 'rose' ? 'text-rose'
          : 'text-text',
        )}
      >
        {value}
      </div>
    </div>
  )
}

function Headline({
  icon, tone, label,
}: {
  icon: React.ReactNode
  tone: 'pulse' | 'amber' | 'rose'
  label: string
}) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 text-sm font-medium',
        tone === 'pulse' ? 'text-pulse'
        : tone === 'amber' ? 'text-amber'
        : 'text-rose',
      )}
    >
      {icon}
      <span>{label}</span>
    </div>
  )
}

function CheckedAt({ iso }: { iso: string }) {
  const { t } = useTranslation('app')
  return (
    <div className="text-xs text-textFaint font-mono">
      {t('bybit.card.last_check', { ago: timeAgo(iso) })}
    </div>
  )
}
